import type { IPlace } from "./types";

const GEOCODE_TIMEOUT_MS = 3000;

/**
 * Reverse-geocode a free-text address into the IPlace shape that
 * AddressAutocompleteQuestion writes. Returns null when the result
 * is missing, low-confidence, the call fails, or the call hangs
 * past GEOCODE_TIMEOUT_MS (3s). Google's Geocoder silently hangs
 * forever on invalid API keys instead of erroring — the timeout is
 * what keeps the UI from stalling on Done.
 */
export async function reverseGeocode(
  geocoder: google.maps.Geocoder,
  addressString: string,
  timeoutMs: number = GEOCODE_TIMEOUT_MS,
): Promise<IPlace | null> {
  if (!addressString) return null;

  const geocodePromise = geocoder
    .geocode({ address: addressString })
    .catch((err: unknown) => {
      console.warn("[scanner] geocode rejected:", err);
      return null;
    });

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      console.warn(
        `[scanner] geocode timed out after ${timeoutMs}ms — leaving address unfilled`,
      );
      resolve(null);
    }, timeoutMs);
  });

  const response = await Promise.race([geocodePromise, timeoutPromise]);
  if (timer) clearTimeout(timer);
  if (!response || !("results" in response)) return null;

  // The Promise-returning Geocoder typings only expose `results`, but legacy
  // callback responses (and our test mocks) also carry a `status` field. If
  // it's present, reject anything other than OK as the docstring promises.
  const status = (response as { status?: string }).status;
  if (status !== undefined && status !== "OK") return null;

  const results = response.results ?? [];
  if (results.length === 0) return null;

  const first = results[0];
  if (first.partial_match) return null;
  if (!first.place_id || !first.formatted_address) return null;

  return {
    address_components: (first.address_components ?? []).map((c) => ({
      long_name: c.long_name,
      short_name: c.short_name,
      types: c.types,
    })),
    formatted_address: first.formatted_address,
    geometry: {
      lat: first.geometry.location.lat(),
      lng: first.geometry.location.lng(),
    },
    name: first.formatted_address,
    // Maps Search URL that accepts a place_id (`cid` is a different
    // Maps identifier — passing a place_id there produces a broken URL).
    // Docs: https://developers.google.com/maps/documentation/urls/get-started#search-action
    url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      first.formatted_address,
    )}&query_place_id=${encodeURIComponent(first.place_id)}`,
    types: first.types ?? [],
    place_id: first.place_id,
  };
}
